class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    if (facetForm.id === 'FacetFiltersFormMobile') {
      const drawerDetails = facetForm.closest('details');
      if (drawerDetails) {
        drawerDetails.addEventListener('toggle', () => {
          if (drawerDetails.open) {
            this.captureMobileDrawerState();
          } else {
            this.resetMobileDrawerState();
          }
        });
      }

      facetForm.addEventListener('click', (event) => {
        const filterButton = event.target.closest(
          '[data-facets-apply], [data-facets-clear], [data-price-preset], [data-mobile-filter-chip]'
        );

        if (!filterButton) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        if (filterButton.matches('[data-facets-apply]')) {
          this.onDrawerApply(event);
          return;
        }
        if (filterButton.matches('[data-facets-clear]')) {
          this.onDrawerClear(event);
          return;
        }
        if (filterButton.matches('[data-price-preset]')) {
          this.onPricePresetClick(event);
          return;
        }
        if (filterButton.matches('[data-mobile-filter-chip]')) {
          this.onMobileChipClick(event);
        }
      }, true);
      facetForm.addEventListener('change', (event) => {
        if (event.target.matches('[data-in-stock-toggle]')) this.onInStockToggle(event);
        this.setMobileApplyState(true);
        this.renderMobileFilterChips(facetForm);
      });
      facetForm.addEventListener('input', (event) => {
        if (event.target.matches('[data-brand-filter-search]')) {
          this.onBrandSearch(event);
          return;
        }
        this.setMobileApplyState(true);
        this.renderMobileFilterChips(facetForm);
      });
      this.setMobileApplyState(false);
      this.renderMobileFilterChips(facetForm);
    } else {
      facetForm.addEventListener('input', (event) => {
        if (event.target.matches('[data-brand-filter-search]')) {
          this.onBrandSearch(event);
          return;
        }
        this.debouncedOnSubmit(event);
      });
    }
    facetForm.addEventListener('change', (event) => {
      if (event.target.matches('[data-show-unavailable-toggle]')) this.onUnavailableToggle(event);
    });
    facetForm.addEventListener('click', (event) => {
      const sortOption = event.target.closest('[data-sort-option]');
      if (!sortOption) return;

      event.preventDefault();
      const select = facetForm.querySelector('[name="sort_by"]');
      const details = sortOption.closest('details');
      const selectedText = details?.querySelector('.unified-sort__button span:first-child');

      if (!select) return;

      select.value = sortOption.dataset.sortValue;
      if (selectedText) selectedText.textContent = sortOption.textContent.trim();
      details?.removeAttribute('open');

      facetForm.querySelectorAll('[data-sort-option]').forEach((option) => {
        option.classList.toggle('is-active', option === sortOption);
      });

      this.onSubmitHandler(event);
    });
    FacetFiltersForm.syncUnavailableToggleState();

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    const fetchSearchParams = FacetFiltersForm.getFetchSearchParams(searchParams);
    const publicSearchParams = FacetFiltersForm.getPublicSearchParams(fetchSearchParams);

    FacetFiltersForm.searchParamsPrev = publicSearchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${fetchSearchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(publicSearchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.syncUnavailableToggleState();
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.syncUnavailableToggleState();
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    FacetFiltersForm.renderMobileDrawer(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter')?.id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderMobileDrawer(html) {
    const sourceInner = html.querySelector('#FacetFiltersFormMobile .mobile-facets__inner');
    const targetInner = document.querySelector('#FacetFiltersFormMobile .mobile-facets__inner');
    if (!sourceInner || !targetInner) return;

    targetInner.innerHTML = sourceInner.innerHTML;

    const form = document.getElementById('FacetFiltersFormMobile');
    const drawerController = form?.closest('facet-filters-form');
    drawerController?.setMobileApplyState(false);
    drawerController?.syncMobilePricePresets(form);
    drawerController?.renderMobileFilterChips(form);
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getFetchSearchParams(searchParams) {
    const params = new URLSearchParams(searchParams);
    const showUnavailable = params.get('filter.v.unavailability') === '1';

    if (showUnavailable) {
      params.delete('filter.v.availability');
    } else {
      params.set('filter.v.availability', '1');
    }

    return params.toString();
  }

  static getPublicSearchParams(searchParams) {
    const params = new URLSearchParams(searchParams);

    if (params.get('filter.v.availability') === '1') {
      params.delete('filter.v.availability');
    }

    return params.toString();
  }

  static syncUnavailableToggleState() {
    const showUnavailable = new URLSearchParams(window.location.search).get('filter.v.unavailability') === '1';
    document.documentElement.classList.toggle('show-unavailable-products', showUnavailable);
    document.querySelectorAll('[data-show-unavailable-toggle]').forEach((input) => {
      input.checked = showUnavailable;
      const form = input.closest('form');
      const hiddenInput = form?.querySelector('[data-unavailability-input]');
      if (hiddenInput) hiddenInput.disabled = !showUnavailable;
    });
    document.querySelectorAll('[data-in-stock-toggle]').forEach((input) => {
      input.checked = !showUnavailable;
      const form = input.closest('form');
      const hiddenInput = form?.querySelector('[data-unavailability-input]');
      if (hiddenInput) hiddenInput.disabled = input.checked;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    const drawerController = mobileForm?.closest('facet-filters-form');
    drawerController?.renderMobileFilterChips(mobileForm);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  setMobileApplyState(isDirty) {
    const mobileForm = this.querySelector('#FacetFiltersFormMobile');
    if (!mobileForm) return;

    mobileForm.dataset.filtersDirty = isDirty ? 'true' : 'false';
    mobileForm.querySelectorAll('[data-facets-apply]').forEach((button) => {
      button.disabled = !isDirty;
      button.classList.toggle('button--disabled', !isDirty);
    });
  }

  captureMobileDrawerState() {
    const mobileForm = this.querySelector('#FacetFiltersFormMobile');
    const mobileInner = mobileForm?.querySelector('.mobile-facets__inner');
    if (!mobileForm || !mobileInner) return;

    mobileForm.dataset.filtersApplied = 'false';
    mobileForm.dataset.drawerSnapshot = mobileInner.innerHTML;
  }

  resetMobileDrawerState() {
    const mobileForm = this.querySelector('#FacetFiltersFormMobile');
    const mobileInner = mobileForm?.querySelector('.mobile-facets__inner');
    const snapshot = mobileForm?.dataset.drawerSnapshot;
    if (!mobileForm || !mobileInner || !snapshot || mobileForm.dataset.filtersApplied === 'true') return;

    mobileInner.innerHTML = snapshot;
    this.setMobileApplyState(false);
    this.syncMobilePricePresets(mobileForm);
    this.renderMobileFilterChips(mobileForm);
  }


  syncMobilePricePresets(form) {
    if (!form) return;

    const minPrice = form.querySelector('input[name="filter.v.price.gte"]')?.value?.trim() || '';
    const maxPrice = form.querySelector('input[name="filter.v.price.lte"]')?.value?.trim() || '';

    form.querySelectorAll('[data-price-preset]').forEach((preset) => {
      preset.classList.toggle(
        'is-active',
        (preset.dataset.priceMin || '') === minPrice && (preset.dataset.priceMax || '') === maxPrice
      );
    });
  }

  onBrandSearch(event) {
    const query = event.target.value.trim().toLowerCase();
    const filterScope =
      event.target.closest('.mobile-facets__submenu') ||
      event.target.closest('.facets-wrap') ||
      event.target.closest('.js-filter');

    if (!filterScope) return;

    filterScope.querySelectorAll('.facets__item, .mobile-facets__item').forEach((item) => {
      const text = item.textContent.trim().toLowerCase();
      item.classList.toggle('hidden', Boolean(query) && !text.includes(query));
    });
  }

  renderMobileFilterChips(form) {
    const chipContainer = form.querySelector('[data-mobile-filter-chips]');
    const emptyMessage = form.querySelector('[data-mobile-filter-empty]');
    const clearButton = form.querySelector('[data-facets-clear]');
    if (!chipContainer) return;

    const chips = [];
    let hasClearableFilters = false;
    const inStockToggle = form.querySelector('[data-in-stock-toggle]');

    if (inStockToggle?.checked) {
      chips.push({ label: 'In Stock', target: 'in-stock' });
    } else {
      hasClearableFilters = true;
    }

    form.querySelectorAll('input[type="checkbox"]:checked:not([data-in-stock-toggle])').forEach((input) => {
      if (!input.name || input.disabled) return;
      const label = input.closest('label')?.querySelector('.facet-checkbox__text-label')?.textContent?.trim();
      if (label) {
        chips.push({ label, target: input.name, value: input.value });
        hasClearableFilters = true;
      }
    });

    const minPriceInput = form.querySelector('input[name="filter.v.price.gte"]');
    const maxPriceInput = form.querySelector('input[name="filter.v.price.lte"]');
    const minPrice = minPriceInput?.value?.trim();
    const maxPrice = maxPriceInput?.value?.trim();

    if (minPrice || maxPrice) {
      chips.push({
        label: `${minPrice ? `$${minPrice}` : '$0'} - ${maxPrice ? `$${maxPrice}` : 'Any'}`,
        target: 'price',
      });
      hasClearableFilters = true;
    }

    chipContainer.innerHTML = chips
      .map(
        (chip) =>
          `<button type="button" class="mobile-facets__chip" data-mobile-filter-chip data-chip-target="${this.escapeHTML(
            chip.target
          )}"${chip.value ? ` data-chip-value="${this.escapeHTML(chip.value)}"` : ''}>${this.escapeHTML(
            chip.label
          )} <span aria-hidden="true">×</span></button>`
      )
      .join('');

    const hasChips = chips.length > 0;
    emptyMessage?.classList.toggle('hidden', hasChips);
    clearButton?.classList.toggle('hidden', !hasClearableFilters);
  }

  escapeHTML(value) {
    return String(value).replace(/[&<>"']/g, (character) => {
      const entities = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
      return entities[character];
    });
  }

  onInStockToggle(event) {
    const form = event.target.closest('form');
    const hiddenInput = form?.querySelector('[data-unavailability-input]');
    if (hiddenInput) hiddenInput.disabled = event.target.checked;
  }

  onPricePresetClick(event) {
    event.preventDefault();
    event.stopPropagation();
    const button = event.target.closest('[data-price-preset]');
    const form = button.closest('form');
    const minPriceInput = form.querySelector('input[name="filter.v.price.gte"]');
    const maxPriceInput = form.querySelector('input[name="filter.v.price.lte"]');

    if (minPriceInput) minPriceInput.value = button.dataset.priceMin || '';
    if (maxPriceInput) maxPriceInput.value = button.dataset.priceMax || '';

    form.querySelectorAll('[data-price-preset]').forEach((preset) => preset.classList.remove('is-active'));
    button.classList.add('is-active');
    this.setMobileApplyState(true);
    this.renderMobileFilterChips(form);
  }

  onDrawerClear(event) {
    event.preventDefault();
    const form = event.target.closest('form');
    if (!form) return;

    form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      input.checked = input.matches('[data-in-stock-toggle]');
    });

    form.querySelectorAll('input[type="text"], input[type="number"], input[type="search"]').forEach((input) => {
      input.value = '';
    });

    form.querySelectorAll('[data-price-preset]').forEach((preset) => preset.classList.remove('is-active'));
    form.querySelectorAll('.facets__item.hidden, .mobile-facets__item.hidden').forEach((item) => {
      item.classList.remove('hidden');
    });

    const hiddenInput = form.querySelector('[data-unavailability-input]');
    if (hiddenInput) hiddenInput.disabled = true;

    this.setMobileApplyState(true);
    this.renderMobileFilterChips(form);
  }

  onMobileChipClick(event) {
    event.preventDefault();
    const chip = event.target.closest('[data-mobile-filter-chip]');
    const form = chip.closest('form');
    const target = chip.dataset.chipTarget;

    if (target === 'in-stock') {
      const inStockToggle = form.querySelector('[data-in-stock-toggle]');
      if (inStockToggle) {
        inStockToggle.checked = false;
        this.onInStockToggle({ target: inStockToggle });
      }
    } else if (target === 'price') {
      form.querySelectorAll('input[name="filter.v.price.gte"], input[name="filter.v.price.lte"]').forEach((input) => {
        input.value = '';
      });
      form.querySelectorAll('[data-price-preset]').forEach((preset) => preset.classList.remove('is-active'));
    } else {
      const input = Array.from(form.querySelectorAll('input[type="checkbox"]')).find(
        (checkbox) => checkbox.name === target && checkbox.value === (chip.dataset.chipValue || '')
      );
      if (input) input.checked = false;
    }

    this.setMobileApplyState(true);
    this.renderMobileFilterChips(form);
  }

  onDrawerApply(event) {
    event.preventDefault();
    const form = event.target.closest('form');
    const searchParams = this.createSearchParams(form);
    const params = new URLSearchParams(searchParams);
    params.delete('page');
    form.dataset.filtersApplied = 'true';
    this.setMobileApplyState(false);

    const targetUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
    window.location.assign(targetUrl);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    if (event.target.matches('[data-show-unavailable-toggle]')) return;

    const submittedForm = event.target.closest('form');
    if (submittedForm.id === 'FacetSortDrawerForm') {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('sort_by', submittedForm.querySelector('[name="sort_by"]').value);
      this.onSubmitForm(searchParams.toString(), event);
      return;
    }

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }

  onUnavailableToggle(event) {
    event.preventDefault();
    const form = event.target.closest('form');
    const hiddenInput = form?.querySelector('[data-unavailability-input]');

    if (hiddenInput) hiddenInput.disabled = !event.target.checked;

    if (form?.id === 'FacetFiltersFormMobile') {
      document.documentElement.classList.toggle('show-unavailable-products', event.target.checked);
      this.setMobileApplyState(true);
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);

    searchParams.delete('page');
    searchParams.delete('filter.v.availability');

    if (event.target.checked) {
      searchParams.set('filter.v.unavailability', '1');
    } else {
      searchParams.delete('filter.v.unavailability');
    }

    document.documentElement.classList.toggle('show-unavailable-products', event.target.checked);
    this.onSubmitForm(searchParams.toString(), event);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
